import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { Images, Trash, SpinnerGap, DownloadSimple, Eye, X, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function ImageLibraryPage() {
  const { api } = useAuth();
  const [images, setImages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [blobUrls, setBlobUrls] = useState({});

  const fetchLibrary = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const resp = await api.get(`/graphics/library?page=${p}&limit=20`);
      setImages(resp.data.images || []);
      setTotal(resp.data.total || 0);
      setPage(resp.data.page || 1);
      setPages(resp.data.pages || 1);
    } catch (e) {
      toast.error("Failed to load image library");
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchLibrary(1); }, [fetchLibrary]);

  const fetchBlob = useCallback(async (genId) => {
    if (blobUrls[genId]) return blobUrls[genId];
    try {
      const resp = await api.get(`/graphics/image/${genId}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setBlobUrls(prev => ({ ...prev, [genId]: url }));
      return url;
    } catch { return null; }
  }, [api, blobUrls]);

  useEffect(() => {
    images.forEach(img => { if (!blobUrls[img.gen_id]) fetchBlob(img.gen_id); });
  }, [images, blobUrls, fetchBlob]);

  const handleDelete = async (genId) => {
    if (!window.confirm("Remove this image from your library?")) return;
    setDeleting(genId);
    try {
      await api.delete(`/graphics/library/${genId}`);
      setImages(prev => prev.filter(i => i.gen_id !== genId));
      setTotal(prev => prev - 1);
      toast.success("Image removed");
    } catch (e) {
      toast.error("Failed to delete image");
    } finally { setDeleting(null); }
  };

  const handleDownload = async (genId, topic) => {
    const url = blobUrls[genId] || await fetchBlob(genId);
    if (!url) { toast.error("Failed to download"); return; }
    const a = document.createElement("a");
    a.href = url;
    a.download = `graphic_${topic?.replace(/[^a-z0-9]/gi, "_").slice(0, 30) || genId}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <>
      <Header />
      <NoBusinessGuard>
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
          {/* Hero */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
                  <Images size={22} weight="duotone" className="text-violet-400" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight" data-testid="library-title">
                  Image Library
                </h1>
              </div>
              <p className="text-sm text-[var(--text-muted)]">{total} saved graphic{total !== 1 ? "s" : ""}</p>
            </div>
            <Link to="/graphics" data-testid="library-create-btn">
              <Button className="bg-violet-600 hover:bg-violet-500 text-white font-medium rounded-xl gap-2">
                <Images size={16} /> Create New Graphic
              </Button>
            </Link>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <SpinnerGap size={32} className="animate-spin text-violet-400" />
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-20" data-testid="library-empty">
              <Images size={48} className="mx-auto text-[var(--text-muted)] opacity-30 mb-3" />
              <p className="text-sm text-[var(--text-muted)] mb-4">No graphics saved yet</p>
              <Link to="/graphics">
                <Button className="bg-violet-600 hover:bg-violet-500 text-white rounded-xl gap-2">
                  <Images size={16} /> Generate Your First Graphic
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="library-grid">
                <AnimatePresence>
                  {images.map((img) => (
                    <motion.div key={img.gen_id}
                      initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                      className="group bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden hover:border-violet-500/30 transition-all"
                      data-testid={`library-item-${img.gen_id}`}>
                      {/* Image */}
                      <div className="relative aspect-square bg-[var(--bg-base)] flex items-center justify-center cursor-pointer"
                        onClick={() => setLightbox(img)}>
                        {blobUrls[img.gen_id] ? (
                          <img src={blobUrls[img.gen_id]} alt={img.topic || "Graphic"}
                            className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <SpinnerGap size={20} className="animate-spin text-[var(--text-muted)]" />
                        )}
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <Eye size={24} className="text-white" />
                        </div>
                      </div>
                      {/* Meta */}
                      <div className="p-2.5">
                        <p className="text-[11px] font-medium text-[var(--text-primary)] truncate">{img.topic || "Untitled"}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-400 font-medium">{img.visual_style}</span>
                          <span className="text-[9px] text-[var(--text-muted)]">{img.aspect_ratio}</span>
                        </div>
                        {/* Actions */}
                        <div className="flex items-center gap-1.5 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); handleDownload(img.gen_id, img.topic); }}
                            className="p-1.5 rounded-lg bg-[var(--bg-base)] border border-[var(--border)] text-[var(--text-muted)] hover:text-violet-400 transition-colors"
                            title="Download" data-testid={`library-download-${img.gen_id}`}>
                            <DownloadSimple size={12} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(img.gen_id); }}
                            disabled={deleting === img.gen_id}
                            className="p-1.5 rounded-lg bg-[var(--bg-base)] border border-[var(--border)] text-[var(--text-muted)] hover:text-red-400 transition-colors"
                            title="Delete" data-testid={`library-delete-${img.gen_id}`}>
                            {deleting === img.gen_id ? <SpinnerGap size={12} className="animate-spin" /> : <Trash size={12} />}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-8" data-testid="library-pagination">
                  <Button onClick={() => fetchLibrary(page - 1)} disabled={page <= 1} variant="outline" className="h-9 px-3 text-xs rounded-lg gap-1">
                    <CaretLeft size={12} /> Prev
                  </Button>
                  <span className="text-xs text-[var(--text-muted)]">Page {page} of {pages}</span>
                  <Button onClick={() => fetchLibrary(page + 1)} disabled={page >= pages} variant="outline" className="h-9 px-3 text-xs rounded-lg gap-1">
                    Next <CaretRight size={12} />
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Lightbox */}
          <AnimatePresence>
            {lightbox && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                onClick={() => setLightbox(null)} data-testid="library-lightbox">
                <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
                  className="relative max-w-3xl w-full max-h-[90vh] bg-[var(--bg-card)] rounded-2xl overflow-hidden border border-[var(--border)]"
                  onClick={e => e.stopPropagation()}>
                  {/* Close */}
                  <button onClick={() => setLightbox(null)}
                    className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors" data-testid="lightbox-close">
                    <X size={18} />
                  </button>
                  {/* Image */}
                  <div className="flex items-center justify-center bg-[var(--bg-base)] max-h-[60vh] overflow-hidden">
                    {blobUrls[lightbox.gen_id] ? (
                      <img src={blobUrls[lightbox.gen_id]} alt={lightbox.topic || "Graphic"} className="w-full h-auto object-contain max-h-[60vh]" />
                    ) : (
                      <SpinnerGap size={32} className="animate-spin text-violet-400 my-20" />
                    )}
                  </div>
                  {/* Info */}
                  <div className="p-4 sm:p-5 space-y-2">
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">{lightbox.topic || "Untitled"}</h3>
                    <div className="flex flex-wrap gap-2 text-[10px]">
                      <span className="px-2 py-0.5 rounded bg-violet-500/10 text-violet-400 font-medium">{lightbox.visual_style}</span>
                      <span className="px-2 py-0.5 rounded bg-[var(--bg-base)] text-[var(--text-muted)]">{lightbox.aspect_ratio}</span>
                      {lightbox.graphic_purpose && <span className="px-2 py-0.5 rounded bg-[var(--bg-base)] text-[var(--text-muted)]">{lightbox.graphic_purpose}</span>}
                      {lightbox.industry_niche && <span className="px-2 py-0.5 rounded bg-[var(--bg-base)] text-[var(--text-muted)]">{lightbox.industry_niche}</span>}
                    </div>
                    {/* Color swatches */}
                    <div className="flex items-center gap-2">
                      {[lightbox.background_color, lightbox.primary_color, lightbox.text_color, lightbox.accent_color].filter(Boolean).map((c) => (
                        <div key={`color-${c}`} className="flex items-center gap-1.5">
                          <div className="w-4 h-4 rounded-full border border-white/20" style={{ backgroundColor: c }} />
                          <span className="text-[10px] font-mono text-[var(--text-muted)]">{c}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button onClick={() => handleDownload(lightbox.gen_id, lightbox.topic)}
                        className="bg-violet-600 hover:bg-violet-500 text-white text-xs h-9 rounded-lg gap-1.5" data-testid="lightbox-download">
                        <DownloadSimple size={14} /> Download
                      </Button>
                      <Button onClick={() => { handleDelete(lightbox.gen_id); setLightbox(null); }}
                        variant="outline" className="text-xs h-9 rounded-lg gap-1.5 text-red-400 border-red-400/30 hover:bg-red-500/10" data-testid="lightbox-delete">
                        <Trash size={14} /> Delete
                      </Button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </NoBusinessGuard>
    </>
  );
}
